import { useQuery } from '@tanstack/react-query';
import { useEffect, type JSX } from 'react';
import { Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useZodForm } from '@/lib/forms/useZodForm';
import { listRolesRequest } from '@/services/roles';
import { listUsersRequest } from '@/services/users';
import type { PaginatedResult, RoleListItem } from '@/types/roles';
import type { UserListItem } from '@/types/users';

/**
 * Plan 09-02 D-88 SiteForm — shared between create and edit modes.
 *
 * Schema rules (mirror Plan 09-01 backend validation):
 *  - name: 3..200 chars
 *  - code: 2..30 alnum + hyphens (regex `^[A-Za-z0-9-]+$`)
 *  - location: 1..200 chars
 *  - address: optional, ≤500 chars
 *  - description: optional, ≤1000 chars
 *  - siteHeadUserId: optional positive int
 *  - isActive: boolean (default true)
 *
 * The Site Head dropdown is sourced via a chained query:
 *  1. Resolve the Site Head role id via `listRolesRequest({ q: 'Site Head' })`.
 *  2. Once known, list active users in that role via
 *     `listUsersRequest({ roleId, status: 'active', pageSize: 100 })`.
 *
 * The dropdown is empty when no Site Head users exist (acceptable per
 * CONTEXT D-86 — sites can be created without a head).
 */

const SITE_CODE_REGEX = /^[A-Za-z0-9-]+$/;

const siteFormSchema = z
  .object({
    name: z.string().min(3, 'At least 3 characters').max(200, 'At most 200 characters'),
    code: z
      .string()
      .min(2, 'At least 2 characters')
      .max(30, 'At most 30 characters')
      .regex(SITE_CODE_REGEX, 'Letters, digits, and hyphens only'),
    location: z.string().min(1, 'Required').max(200, 'At most 200 characters'),
    address: z
      .string()
      .max(500, 'At most 500 characters')
      .optional()
      .or(z.literal('').transform(() => undefined)),
    description: z
      .string()
      .max(1000, 'At most 1000 characters')
      .optional()
      .or(z.literal('').transform(() => undefined)),
    siteHeadUserId: z
      .number({ invalid_type_error: 'Pick a site head' })
      .int()
      .positive()
      .optional()
      .nullable(),
    isActive: z.boolean(),
  })
  .strict();

export type SiteFormValues = z.infer<typeof siteFormSchema>;

export interface SiteFormProps {
  readonly mode: 'create' | 'edit';
  readonly initialValues?: Partial<SiteFormValues>;
  readonly onSubmit: (values: SiteFormValues) => void | Promise<void>;
  readonly submitting?: boolean;
  readonly errorMessage?: string | null;
}

const EMPTY_DEFAULTS: Partial<SiteFormValues> = {
  name: '',
  code: '',
  location: '',
  address: '',
  description: '',
  siteHeadUserId: null,
  isActive: true,
};

export function SiteForm({
  mode,
  initialValues,
  onSubmit,
  submitting = false,
  errorMessage,
}: SiteFormProps): JSX.Element {
  const defaults: Partial<SiteFormValues> = {
    ...EMPTY_DEFAULTS,
    ...initialValues,
  };

  const form = useZodForm({
    schema: siteFormSchema,
    defaultValues: defaults,
  });

  // Re-sync defaults when initialValues changes (SiteEditPage hydrates async).
  useEffect(() => {
    if (initialValues !== undefined) {
      form.reset({ ...EMPTY_DEFAULTS, ...initialValues });
    }
    // form.reset is stable; intentionally only depend on initialValues identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  // Stage 1: resolve the "Site Head" role id by name (idempotent across forms).
  const siteHeadRoleQuery = useQuery<PaginatedResult<RoleListItem>>({
    queryKey: ['roles', 'siteHeadRole'],
    queryFn: () => listRolesRequest({ q: 'Site Head', status: 'active', page: 1, pageSize: 10 }),
  });

  const siteHeadRoleId = siteHeadRoleQuery.data?.items.find((r) => r.name === 'Site Head')?.id;

  // Stage 2: once we know the role id, fetch active users in that role.
  const siteHeadsQuery = useQuery<PaginatedResult<UserListItem>>({
    queryKey: ['users', 'siteHeads', siteHeadRoleId ?? 0],
    queryFn: () => {
      const roleId = siteHeadRoleId ?? 0;
      return listUsersRequest({
        roleId,
        status: 'active',
        page: 1,
        pageSize: 100,
      });
    },
    enabled: typeof siteHeadRoleId === 'number' && siteHeadRoleId > 0,
  });

  const siteHeadOptions = siteHeadsQuery.data?.items ?? [];

  return (
    <form
      onSubmit={(e) => {
        void form.handleSubmit(onSubmit)(e);
      }}
      className="space-y-6"
      noValidate
      data-testid={`site-form-${mode}`}
    >
      {errorMessage != null && errorMessage !== '' && (
        <div
          role="alert"
          data-testid="site-form-error"
          className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {errorMessage}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="site-name">Name</Label>
          <Input
            id="site-name"
            type="text"
            autoComplete="off"
            disabled={submitting}
            aria-invalid={form.formState.errors.name !== undefined}
            {...form.register('name')}
            data-testid="site-form-name"
          />
          {form.formState.errors.name && (
            <p className="text-xs text-destructive" data-testid="site-form-name-error">
              {form.formState.errors.name.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="site-code">Code</Label>
          <Input
            id="site-code"
            type="text"
            autoComplete="off"
            disabled={submitting}
            aria-invalid={form.formState.errors.code !== undefined}
            {...form.register('code')}
            data-testid="site-form-code"
          />
          {form.formState.errors.code && (
            <p className="text-xs text-destructive" data-testid="site-form-code-error">
              {form.formState.errors.code.message}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="site-location">Location</Label>
        <Input
          id="site-location"
          type="text"
          autoComplete="off"
          disabled={submitting}
          aria-invalid={form.formState.errors.location !== undefined}
          {...form.register('location')}
          data-testid="site-form-location"
        />
        {form.formState.errors.location && (
          <p className="text-xs text-destructive" data-testid="site-form-location-error">
            {form.formState.errors.location.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="site-address">Address (optional)</Label>
        <Textarea
          id="site-address"
          rows={2}
          disabled={submitting}
          aria-invalid={form.formState.errors.address !== undefined}
          {...form.register('address')}
          data-testid="site-form-address"
        />
        {form.formState.errors.address && (
          <p className="text-xs text-destructive" data-testid="site-form-address-error">
            {form.formState.errors.address.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="site-description">Description (optional)</Label>
        <Textarea
          id="site-description"
          rows={3}
          disabled={submitting}
          aria-invalid={form.formState.errors.description !== undefined}
          {...form.register('description')}
          data-testid="site-form-description"
        />
        {form.formState.errors.description && (
          <p className="text-xs text-destructive" data-testid="site-form-description-error">
            {form.formState.errors.description.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="site-sitehead">Site Head (optional)</Label>
        <Controller
          control={form.control}
          name="siteHeadUserId"
          render={({ field }) => (
            <Select
              value={
                typeof field.value === 'number' && field.value > 0 ? String(field.value) : 'none'
              }
              onValueChange={(v) => {
                if (v === 'none') {
                  field.onChange(null);
                } else {
                  const n = Number(v);
                  if (Number.isFinite(n) && n > 0) field.onChange(n);
                }
              }}
              disabled={submitting || siteHeadsQuery.isLoading}
            >
              <SelectTrigger id="site-sitehead" data-testid="site-form-sitehead-trigger">
                <SelectValue placeholder="Select a Site Head…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none" data-testid="site-form-sitehead-option-none">
                  — No Site Head —
                </SelectItem>
                {siteHeadOptions.map((u) => (
                  <SelectItem
                    key={u.id}
                    value={String(u.id)}
                    data-testid={`site-form-sitehead-option-${String(u.id)}`}
                  >
                    {u.fullName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        <p className="text-xs text-muted-foreground">
          Only active users in the &quot;Site Head&quot; role appear here.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <input
          id="site-active"
          type="checkbox"
          disabled={submitting}
          {...form.register('isActive')}
          data-testid="site-form-active"
          className="h-4 w-4 rounded border-input"
        />
        <Label htmlFor="site-active">Active</Label>
      </div>

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submitting} data-testid="site-form-submit">
          {submitting
            ? mode === 'create'
              ? 'Creating…'
              : 'Saving…'
            : mode === 'create'
              ? 'Create site'
              : 'Save changes'}
        </Button>
        <Button asChild variant="outline" type="button">
          <Link to="/sites" data-testid="site-form-cancel">
            Cancel
          </Link>
        </Button>
      </div>
    </form>
  );
}
